/**
 * Which air streams the chart draws — the legend's own checkboxes.
 *
 * The backend labels every chart state with a COARSE air stream
 * (`air_stream_label`), and psychrometric_views.py's assemble() guarantees it
 * is one of exactly four values: 'Supply' | 'Return' | 'Exhaust' | 'No Air
 * Stream'. A finer split (a 'Supply 1'/'Supply CD' sub-stream) lands in
 * `table_group` instead and never reaches here, so this vocabulary is closed.
 *
 * Only the three streams the legend names are toggleable. 'No Air Stream' is
 * not in the legend and has no checkbox, so isStreamVisible() keeps it drawn
 * whatever the three boxes say — an untagged section must not be able to
 * vanish with no control to bring it back.
 *
 * Deliberately dependency-free. Not because anything here runs at page load —
 * getPsyStreamVisibility has exactly one caller, the popup's own lazy useState
 * initialiser — but because of the import chain above it: psychrometricStorage
 * imports this module (for normalizeStreamVisibility), and the board-level
 * usePsychrometrics hook imports psychrometricStorage (for the popup-state
 * key). That puts this module on the page-load path, so importing chartTraces
 * or the vendored Plotly types from here would drag them along with it.
 */

/** The legend's three checkboxes, in the order they are rendered. */
export const PSY_TOGGLEABLE_STREAMS = ['Supply', 'Return', 'Exhaust'] as const;

export type PsyToggleableStream = (typeof PSY_TOGGLEABLE_STREAMS)[number];

/** One flag per toggleable stream. Always complete — never a partial map, so
 * a reader never has to decide what `undefined` means. */
export type PsyStreamVisibility = Record<PsyToggleableStream, boolean>;

export const ALL_STREAMS_VISIBLE: PsyStreamVisibility = {
  Supply: true,
  Return: true,
  Exhaust: true,
};

/** Whether a state carrying `label` is drawn. An unknown or absent label —
 * 'No Air Stream', or a value added server-side that this build predates —
 * is drawn, same as before there were any checkboxes. */
export function isStreamVisible(
  visibility: PsyStreamVisibility,
  label: string | null | undefined
): boolean {
  if (label == null) return true;
  const known = (PSY_TOGGLEABLE_STREAMS as readonly string[]).indexOf(label) !== -1;
  return known ? visibility[label as PsyToggleableStream] : true;
}

/** True when nothing is hidden — lets callers skip the filtering work
 * entirely, which is the usual case. */
export function allStreamsVisible(visibility: PsyStreamVisibility): boolean {
  return PSY_TOGGLEABLE_STREAMS.every((stream) => visibility[stream]);
}

/** Coerce an unknown parsed value into a complete visibility map. Anything
 * missing or non-boolean reads as visible: a corrupt stored value must fail
 * towards showing the whole chart, never towards silently hiding part of it. */
export function normalizeStreamVisibility(raw: unknown): PsyStreamVisibility {
  const source = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const out = { ...ALL_STREAMS_VISIBLE };
  PSY_TOGGLEABLE_STREAMS.forEach((stream) => {
    if (source[stream] === false) out[stream] = false;
  });
  return out;
}
